import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import {
  Receipt,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  RefreshCw,
  Filter,
  Wallet,
  Search,
  ArrowRightLeft,
  EyeOff,
  Eye,
  ChevronLeft,
  ChevronRight,
  Brain
} from 'lucide-react';
import apiService from '../services/api';
import TransactionDetailsModal from './modals/TransactionDetailsModal';
import BulkCategoryUpdateModal from './modals/BulkCategoryUpdateModal';
import ReceiptModal from './modals/ReceiptModal';
import AiAnalysisModal from './modals/AiAnalysisModal';
import PageHeader from './common/PageHeader';
import Pagination from './common/Pagination';
import { useToast } from '../hooks/useToast';

const TransactionsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionRefs = useRef({});
  const { showToast, ToastContainer } = useToast();

  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [hideTransfers, setHideTransfers] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [potentialTransfers, setPotentialTransfers] = useState([]);
  const [detectingTransfers, setDetectingTransfers] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [bulkUpdateModal, setBulkUpdateModal] = useState({
    isOpen: false,
    transactionId: null,
    description: '',
    newCategory: '',
    newCategoryIcon: '',
    categoryLabel: ''
  });
  const [highlightedTransaction, setHighlightedTransaction] = useState(null);
  const [viewReceiptModal, setViewReceiptModal] = useState({
    isOpen: false,
    receipt: null,
    currency: 'USD'
  });
  const [isAiAnalysisModalOpen, setIsAiAnalysisModalOpen] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisJobId, setAnalysisJobId] = useState(null);

  useEffect(() => {
    loadAccounts();
    loadCategories();

    // Check for URL parameters and apply filters
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setFilterCategory(categoryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [filterType, filterCategory, searchTerm, hideTransfers, pageSize]);

  useEffect(() => {
    // Load transactions whenever filters or pagination changes
    loadTransactions();
  }, [currentPage, pageSize, filterType, filterCategory, searchTerm, hideTransfers]);

  // Handle transaction query parameter for navigation from receipts
  useEffect(() => {
    const transactionId = searchParams.get('transaction');
    if (transactionId && transactions.length > 0) {
      const id = parseInt(transactionId);

      // Highlight and scroll to the transaction if it's on current page
      setTimeout(() => {
        const element = transactionRefs.current[id];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedTransaction(id);
          setTimeout(() => setHighlightedTransaction(null), 3000);
        }
      }, 100);

      // Clear the query parameter
      setSearchParams({});
    }
  }, [searchParams, transactions]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAllTransactions({
        page: currentPage,
        page_size: pageSize,
        filter_type: filterType !== 'all' ? filterType : null,
        filter_category: filterCategory !== 'all' ? filterCategory : null,
        search_term: searchTerm || null,
        hide_transfers: hideTransfers
      });
      setTransactions(response.transactions);
      setTotalCount(response.pagination.total);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const accountsData = await apiService.getAccounts();
      setAccounts(accountsData);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const categories = await apiService.getCategories();
      const options = categories.map(cat => ({
        value: cat.name,
        label: cat.label,
        icon: cat.icon
      }));
      setCategoryOptions(options);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const handleTransactionSave = async (transactionId, updates) => {
    try {
      // Check if category changed and if we should offer bulk update
      const originalTransaction = transactions.find(tx => tx.id === transactionId);
      const categoryChanged = originalTransaction && updates.ai_category !== originalTransaction.ai_category;

      if (categoryChanged) {
        // Close transaction modal first
        setIsModalOpen(false);

        // Open bulk update modal to ask if user wants to update similar transactions
        const categoryOption = categoryOptions.find(opt => opt.value === updates.ai_category);
        setBulkUpdateModal({
          isOpen: true,
          transactionId: transactionId,
          description: originalTransaction.description,
          newCategory: updates.ai_category,
          newCategoryIcon: updates.category_icon || categoryOption?.icon || '💰',
          categoryLabel: categoryOption?.label || updates.ai_category,
          allUpdates: updates // Store ALL updates, not just category
        });
      } else {
        // Just update the single transaction
        await apiService.updateTransaction(transactionId, updates);
        await loadTransactions();
        await loadAccounts(); // Reload accounts to update balance
      }
    } catch (err) {
      console.error('Error saving transaction:', err);
    }
  };

  const handleTransactionDelete = async (transactionId) => {
    await apiService.deleteTransaction(transactionId);
    await loadTransactions();
    await loadAccounts(); // Reload accounts to update balance
  };

  const closeBulkUpdateModal = () => {
    setBulkUpdateModal({
      isOpen: false,
      transactionId: null,
      description: '',
      newCategory: '',
      newCategoryIcon: '',
      categoryLabel: ''
    });
  };

  const handleBulkUpdateConfirm = async (transactionIds) => {
    try {
      // First, update the original transaction with ALL changes
      if (bulkUpdateModal.allUpdates) {
        await apiService.updateTransaction(bulkUpdateModal.transactionId, bulkUpdateModal.allUpdates);
      }

      // Then bulk update category for similar transactions (excluding the original one)
      const similarTransactionIds = transactionIds.filter(id => id !== bulkUpdateModal.transactionId);
      if (similarTransactionIds.length > 0) {
        await apiService.bulkUpdateTransactionCategory(
          similarTransactionIds,
          bulkUpdateModal.newCategory,
          bulkUpdateModal.newCategoryIcon
        );
      }

      await loadTransactions();
      await loadAccounts(); // Reload accounts to update balance
      closeBulkUpdateModal();
    } catch (err) {
      console.error('Error bulk updating transactions:', err);
    }
  };

  const handleBulkUpdateCancel = async () => {
    try {
      // Use ALL updates, not just category
      await apiService.updateTransaction(bulkUpdateModal.transactionId, bulkUpdateModal.allUpdates || {
        ai_category: bulkUpdateModal.newCategory,
        category_icon: bulkUpdateModal.newCategoryIcon
      });
      await loadTransactions();
      await loadAccounts(); // Reload accounts to update balance
      closeBulkUpdateModal();
    } catch (err) {
      console.error('Error updating transaction:', err);
    }
  };

  const handleDetectTransfers = async () => {
    try {
      setDetectingTransfers(true);
      const result = await apiService.detectPotentialTransfers();
      setPotentialTransfers(result.potential_transfers || []);
      setShowTransferModal(true);
    } catch (err) {
      console.error('Error detecting transfers:', err);
      setError('Failed to detect transfers');
    } finally {
      setDetectingTransfers(false);
    }
  };

  const handleAiAnalysis = () => {
    if (totalCount === 0) {
      showToast('No transactions to analyze. Please import some transactions first.', 'warning');
      return;
    }
    setIsAiAnalysisModalOpen(true);
  };

  const handleStartAnalysis = async (analysisConfig) => {
    // Stream the analysis so it renders live; fall back to the job/poll path
    // on any streaming error.
    setAnalysisResults({ ai_analysis: '' });
    setAnalysisLoading(true);
    let acc = '';
    try {
      await apiService.streamTransactionAnalysis({
        accountId: null,
        config: analysisConfig,
        onDelta: (text) => {
          acc += text;
          setAnalysisResults({ ai_analysis: acc });
        },
        onSummary: (s) => {
          setAnalysisResults((prev) => ({
            ...(prev || {}),
            ai_analysis: acc,
            transaction_count: s.transaction_count,
            total_amount: s.total_amount,
          }));
        },
      });
      setAnalysisLoading(false);
    } catch (err) {
      console.error('Streaming AI analysis failed; falling back to job path:', err);
      try {
        setAnalysisResults(null);
        const response = await apiService.analyzeAllTransactionsWithConfig(analysisConfig);
        setAnalysisJobId(response.job_id);
        pollAnalysisProgress(response.job_id);
      } catch (e2) {
        console.error('Fallback AI analysis failed:', e2);
        setAnalysisLoading(false);
        showToast('Failed to run AI analysis. Please try again.', 'error');
      }
    }
  };

  const pollAnalysisProgress = async (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await apiService.getTaskStatus(jobId);

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          setAnalysisResults(status.result);
          setAnalysisLoading(false);
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setAnalysisLoading(false);
          showToast('Analysis failed. Please try again.', 'error');
        }
      } catch (err) {
        console.error('Error polling analysis status:', err);
      }
    }, 2000);
  };

  const handleLinkTransfer = async (transactionIds) => {
    try {
      await apiService.linkTransfer(transactionIds);
      // Reload transactions to show updated status
      await loadTransactions();
      // Remove this pair from potential transfers
      setPotentialTransfers(prev =>
        prev.filter(pt =>
          !pt.transfer_pair.some(tx => transactionIds.includes(tx.id))
        )
      );
    } catch (err) {
      console.error('Error linking transfer:', err);
    }
  };

  const handleIgnoreTransfer = (transactionIds) => {
    // Simply remove this pair from the list
    setPotentialTransfers(prev =>
      prev.filter(pt =>
        !pt.transfer_pair.some(tx => transactionIds.includes(tx.id))
      )
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // formatDate imported from utils/dateUtils

  const getTransactionIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'deposit':
      case 'credit':
        return TrendingUp;
      case 'withdrawal':
      case 'debit':
        return TrendingDown;
      default:
        return DollarSign;
    }
  };

  const getTransactionColor = (amount) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    // Get full account details for the modal
    setSelectedAccount({
      id: transaction.account_id,
      name: transaction.account_name,
      institution: transaction.account_institution,
      account_type: 'unknown' // We don't have this in the transaction data
    });
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
    setSelectedAccount(null);
  };

  const handleViewReceipt = async (e, receipt) => {
    e.stopPropagation();
    try {
      // Fetch full receipt with items
      const fullReceipt = await apiService.getReceipt(receipt.id);
      setViewReceiptModal({
        isOpen: true,
        receipt: fullReceipt,
        currency: 'USD' // You can get this from the account if needed
      });
    } catch (err) {
      console.error('Error loading receipt:', err);
      // Fallback to showing what we have
      setViewReceiptModal({
        isOpen: true,
        receipt: receipt,
        currency: 'USD'
      });
    }
  };

  const handleCloseReceiptModal = () => {
    setViewReceiptModal({
      isOpen: false,
      receipt: null,
      currency: 'USD'
    });
    loadTransactions(); // Reload in case receipt was unlinked
  };

  // Get unique categories from transactions
  const categories = [...new Set(transactions.map(t => t.ai_category).filter(Boolean))].sort();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <PageHeader
        icon={Receipt}
        title="All Transactions"
        subtitle={`${totalCount} transactions`}
      >
        <button
          onClick={handleAiAnalysis}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-purple-300 dark:border-purple-600 rounded-md shadow-sm text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40"
        >
          <Brain className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">AI Analysis</span>
        </button>
        <button
          onClick={handleDetectTransfers}
          disabled={detectingTransfers}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          <ArrowRightLeft className={`h-4 w-4 ${detectingTransfers ? 'animate-spin' : ''} sm:mr-2`} />
          <span className="hidden sm:inline">Detect Transfers</span>
        </button>
        <button
          onClick={loadTransactions}
          disabled={loading}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''} sm:mr-2`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </PageHeader>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters</h3>
            <button
              onClick={() => setHideTransfers(!hideTransfers)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              {hideTransfers ? (
                <>
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  Show Transfers
                </>
              ) : (
                <>
                  <EyeOff className="h-3.5 w-3.5 mr-1.5" />
                  Hide Transfers
                </>
              )}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search transactions..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
              />
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
            >
              <option value="all">All Types</option>
              <option value="income">Income</option>
              <option value="expense">Expenses</option>
            </select>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 animate-spin mx-auto text-gray-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading transactions...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mx-auto max-w-md">
                <p className="text-red-700 dark:text-red-400">{error}</p>
                <button
                  onClick={loadTransactions}
                  className="mt-4 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No transactions found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                No transactions have been recorded yet.
              </p>
            </div>
          ) : transactions.length === 0 && !loading ? (
            <div className="text-center py-12">
              <Filter className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No matching transactions</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try adjusting your filters or search term.
              </p>
            </div>
          ) : (
            <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {transactions.map((transaction) => {
                    const TransactionIcon = getTransactionIcon(transaction.transaction_type);
                    const isHighlighted = highlightedTransaction === transaction.id;
                    return (
                      <tr
                        key={transaction.id}
                        ref={el => transactionRefs.current[transaction.id] = el}
                        onClick={() => handleTransactionClick(transaction)}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                          isHighlighted ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-500 dark:ring-green-400' : ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDate(transaction.transaction_date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          <div className="flex items-center">
                            {transaction.category_icon && (
                              <span className="mr-2 text-lg">{transaction.category_icon}</span>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {transaction.description || 'Transaction'}
                                </span>
                                {transaction.is_transfer && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                    <ArrowRightLeft className="h-3 w-3 mr-1" />
                                    Transfer
                                  </span>
                                )}
                                {transaction.receipt && (
                                  <button
                                    onClick={(e) => handleViewReceipt(e, transaction.receipt)}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                                    title={`View receipt from ${transaction.receipt.merchant}`}
                                  >
                                    <Receipt className="h-3 w-3 mr-1" />
                                    Receipt
                                  </button>
                                )}
                              </div>
                              {transaction.reference_id && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Ref: {transaction.reference_id}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center">
                            <Wallet className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                            <div>
                              <div className="text-gray-900 dark:text-white">{transaction.account_name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{transaction.account_institution}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {transaction.ai_category && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                              {transaction.ai_category}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                          <span className={getTransactionColor(transaction.total_amount)}>
                            {transaction.total_amount > 0 ? '+' : ''}{formatCurrency(transaction.total_amount)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-2">
              {transactions.map((transaction) => {
                const isHighlighted = highlightedTransaction === transaction.id;
                return (
                <div
                  key={transaction.id}
                  ref={el => transactionRefs.current[transaction.id] = el}
                  onClick={() => handleTransactionClick(transaction)}
                  className={`bg-white dark:bg-gray-800 p-2.5 sm:p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                    isHighlighted ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-500 dark:ring-green-400' : ''
                  }`}
                >
                  {/* Header Row: Date and Amount */}
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(transaction.transaction_date)}
                    </div>
                    <div className={`text-sm sm:text-base font-bold ${getTransactionColor(transaction.total_amount)} whitespace-nowrap`}>
                      {transaction.total_amount > 0 ? '+' : ''}{formatCurrency(transaction.total_amount)}
                    </div>
                  </div>

                  {/* Description with icon */}
                  <div className="flex items-start gap-1.5 mb-1.5">
                    {transaction.category_icon && (
                      <span className="text-base sm:text-lg flex-shrink-0">{transaction.category_icon}</span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                        {transaction.description || 'Transaction'}
                      </div>
                      {/* Account info inline */}
                      <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        <Wallet className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{transaction.account_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Badges and Category Row */}
                  <div className="flex items-center justify-between gap-1.5 flex-wrap">
                    <div className="flex items-center gap-1 flex-wrap">
                      {transaction.ai_category && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs">
                          {transaction.ai_category}
                        </span>
                      )}
                      {transaction.is_transfer && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          <ArrowRightLeft className="h-3 w-3" />
                        </span>
                      )}
                      {transaction.receipt && (
                        <button
                          onClick={(e) => handleViewReceipt(e, transaction.receipt)}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                          title={`View receipt from ${transaction.receipt.merchant}`}
                        >
                          <Receipt className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {transaction.reference_id && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        #{transaction.reference_id}
                      </span>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            </>
          )}
        </div>

        {/* Pagination Controls */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mt-4">
          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && selectedAccount && (
        <TransactionDetailsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          transaction={selectedTransaction}
          categoryOptions={categoryOptions}
          onSave={handleTransactionSave}
          onDelete={handleTransactionDelete}
          onCategoryCreated={loadCategories}
          onTransactionUpdated={loadTransactions}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      )}

      {/* Bulk Category Update Modal */}
      <BulkCategoryUpdateModal
        isOpen={bulkUpdateModal.isOpen}
        description={bulkUpdateModal.description}
        newCategory={bulkUpdateModal.categoryLabel}
        newCategoryIcon={bulkUpdateModal.newCategoryIcon}
        onConfirm={handleBulkUpdateConfirm}
        onClose={handleBulkUpdateCancel}
        accountId={selectedAccount?.id}
        categoryLabel={bulkUpdateModal.categoryLabel}
      />

      {/* View Receipt Modal */}
      <ReceiptModal
        isOpen={viewReceiptModal.isOpen}
        onClose={handleCloseReceiptModal}
        receipt={viewReceiptModal.receipt}
        accounts={accounts}
        currentAccount={viewReceiptModal.receipt ? accounts.find(a => a.id === viewReceiptModal.receipt.account_id) : null}
        onReceiptUpdated={loadTransactions}
      />

      {/* AI Analysis Modal */}
      <AiAnalysisModal
        isOpen={isAiAnalysisModalOpen}
        onClose={() => {
          setIsAiAnalysisModalOpen(false);
          setAnalysisResults(null);
          setAnalysisLoading(false);
        }}
        analysisResults={analysisResults}
        isLoading={analysisLoading}
        onStartAnalysis={handleStartAnalysis}
        categoryOptions={categoryOptions}
      />

      {/* Transfer Detection Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowTransferModal(false)} />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 sm:mx-0 sm:h-10 sm:w-10">
                    <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Potential Transfers Detected
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Found {potentialTransfers.length} potential transfer{potentialTransfers.length !== 1 ? 's' : ''}.
                        Review and link transactions that represent transfers between accounts.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 space-y-4 max-h-96 overflow-y-auto">
                  {potentialTransfers.length === 0 ? (
                    <div className="text-center py-8">
                      <ArrowRightLeft className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500" />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        No potential transfers found
                      </p>
                    </div>
                  ) : (
                    potentialTransfers.map((transfer, index) => {
                      const [tx1, tx2] = transfer.transfer_pair;
                      return (
                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transfer.confidence === 'high'
                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                            }`}>
                              {transfer.confidence === 'high' ? 'High Confidence' : 'Medium Confidence'}
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleIgnoreTransfer([tx1.id, tx2.id])}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                              >
                                Ignore
                              </button>
                              <button
                                onClick={() => handleLinkTransfer([tx1.id, tx2.id])}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Link as Transfer
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Transaction 1 */}
                            <div className="bg-gray-50 dark:bg-gray-900 rounded p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                  Transaction 1
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatDate(tx1.date)}
                                </span>
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center text-sm">
                                  <Wallet className="h-4 w-4 mr-2 text-gray-400" />
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {tx1.account_name}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 ml-6">
                                  {tx1.description}
                                </div>
                                <div className={`text-sm font-medium ml-6 ${getTransactionColor(tx1.amount)}`}>
                                  {formatCurrency(tx1.amount)}
                                </div>
                              </div>
                            </div>

                            {/* Transaction 2 */}
                            <div className="bg-gray-50 dark:bg-gray-900 rounded p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                  Transaction 2
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatDate(tx2.date)}
                                </span>
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center text-sm">
                                  <Wallet className="h-4 w-4 mr-2 text-gray-400" />
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {tx2.account_name}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 ml-6">
                                  {tx2.description}
                                </div>
                                <div className={`text-sm font-medium ml-6 ${getTransactionColor(tx2.amount)}`}>
                                  {formatCurrency(tx2.amount)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default TransactionsPage;
